import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Divider,
  Link,
  List,
  ListItem,
  Paper,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { DoctorRoles, PatientRoles } from '../config/DiactiveTypes';
import patientFaqs from '../data/faqs/patientFaqs';
import doctorFaqs from '../data/faqs/doctorFaqs';

const LINK_PATTERN = /(contacto@diactive\.es|www\.diactive\.es)/g;

const linkify = (text) =>
  text.split(LINK_PATTERN).map((part, i) => {
    if (part === 'contacto@diactive.es') {
      return <Link key={i} href="mailto:contacto@diactive.es">{part}</Link>;
    }
    if (part === 'www.diactive.es') {
      return <Link key={i} href="https://www.diactive.es" target="_blank" rel="noopener noreferrer">{part}</Link>;
    }
    return part;
  });

const renderBlock = (block, i) => {
  if (Array.isArray(block)) {
    return (
      <List key={i} dense disablePadding sx={{ pl: 1 }}>
        {block.map((item, j) => (
          <ListItem key={j} sx={{ display: 'list-item', listStyleType: 'disc', pl: 0, ml: 2.5, py: 0.25, color: 'text.secondary' }}>
            <Typography variant="body1" color="text.secondary">
              {linkify(item)}
            </Typography>
          </ListItem>
        ))}
      </List>
    );
  }
  return (
    <Typography key={i} variant="body1" color="text.secondary" sx={{ mt: i > 0 ? 1.5 : 0 }}>
      {linkify(block)}
    </Typography>
  );
};

const Faq = ({ role, faqType }) => {
  const [expanded, setExpanded] = useState(false);

  const isPatient = faqType === 'patient' || PatientRoles.includes(role);
  const isDoctor = faqType === 'doctor' || DoctorRoles.includes(role);
  const faqs = isPatient ? patientFaqs : isDoctor ? doctorFaqs : [];
  const subtitle = isPatient
    ? 'Respuestas rápidas a las dudas más comunes sobre la plataforma y la app'
    : 'Respuestas rápidas a las dudas más comunes sobre el uso de la plataforma';

  const handleChange = (key) => (_, isExpanded) => {
    setExpanded(isExpanded ? key : false);
  };

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
          <HelpOutlineIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h5" component="h1" fontWeight={700} textAlign="center">
            Preguntas frecuentes
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {faqs.map((item, index) => (
            <Accordion
              key={index}
              expanded={expanded === index}
              onChange={handleChange(index)}
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: expanded === index ? 'primary.main' : 'divider',
                borderRadius: '6px !important',
                '&:before': { display: 'none' },
                overflow: 'hidden',
                transition: 'border-color 0.2s',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon color="primary" />}
                aria-controls={`faq-${index}-content`}
                id={`faq-${index}-header`}
                sx={{
                  backgroundColor: expanded === index ? 'primary.main' : 'transparent',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    backgroundColor: expanded === index ? 'primary.main' : 'action.hover',
                  },
                  px: 2,
                  py: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      minWidth: 28,
                      height: 28,
                      borderRadius: '50%',
                      backgroundColor: expanded === index ? 'rgba(255,255,255,0.25)' : 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700, lineHeight: 1 }}>
                      {index + 1}
                    </Typography>
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: expanded === index ? '#fff' : 'primary.main',
                      transition: 'color 0.2s',
                    }}
                  >
                    {item.question}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2.5, py: 2, backgroundColor: '#fafafa' }}>
                {item.answer.map(renderBlock)}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Paper>
    </Container>
  );
};

export default Faq;
